import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import toast from 'react-hot-toast';
import { supabase } from './supabase';

/**
 * Capacitor-aware OAuth helpers.
 *
 * In the native APK, the default Supabase OAuth flow doesn't work: Google
 * opens the system browser, auths, and redirects to a web URL — but
 * web URL means a browser tab, not back into the app. The WebView
 * never sees the callback and the user is stranded on the login screen.
 *
 * Fix: deep linking. Steps:
 *   1. Register a custom URL scheme (`info.gasweb.streamflow://`) on
 *      Android via an intent-filter in AndroidManifest.xml (added by
 *      our CI workflow's post-sync patch step — see android-apk.yml).
 *   2. Supabase project allowlists `info.gasweb.streamflow://auth-callback`
 *      as a redirect target.
 *   3. SPA calls signInWithOAuth with skipBrowserRedirect=true so we
 *      get the authorize URL back instead of an instant redirect.
 *   4. Open that URL in the system browser via @capacitor/browser.
 *   5. After Google auth, Supabase 302s to info.gasweb.streamflow://...
 *      Android's intent system delivers it to the app.
 *   6. CapApp.appUrlOpen fires with the deep link URL → we close the
 *      in-app browser tab and call exchangeCodeForSession to complete.
 */

export function isNative(): boolean {
  return Capacitor.isNativePlatform();
}

/** Deep-link target Supabase redirects to after Google auth on native.
 * Must match the AndroidManifest.xml intent-filter scheme AND be in
 * the Supabase project's "Redirect URLs" allowlist. */
export const NATIVE_AUTH_CALLBACK = 'info.gasweb.streamflow://auth-callback';

/** Initiate Google OAuth from inside the native APK. Returns an error
 * string on failure; success is signaled later via the appUrlOpen
 * listener calling exchangeCodeForSession. */
export async function signInWithGoogleNative(): Promise<{ error: string | null }> {
  const { data, error } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: {
      redirectTo: NATIVE_AUTH_CALLBACK,
      skipBrowserRedirect: true,
      queryParams: { prompt: 'select_account' },
    },
  });
  if (error || !data?.url) {
    return { error: error?.message ?? 'Failed to start Google sign-in' };
  }
  // Open Google auth in the system browser. The Browser plugin uses
  // Chrome Custom Tabs on Android — a real browser tab, not a WebView
  // — which Google requires (in-app WebViews are blocked by Google
  // with `disallowed_useragent`).
  await Browser.open({ url: data.url, presentationStyle: 'popover' });
  return { error: null };
}

/** Mount the deep-link listener that completes OAuth after Google
 * redirects back via the custom scheme. Call once at app startup
 * (inside AuthProvider's useEffect). Returns a teardown function. */
export function installDeepLinkAuthHandler(): () => void {
  if (!isNative()) return () => {};

  const cleanup = CapApp.addListener('appUrlOpen', async ({ url }) => {
    if (!url.startsWith(NATIVE_AUTH_CALLBACK)) return;
    // Close the Chrome Custom Tab so the user lands back in the app.
    try { await Browser.close(); } catch { /* tab already gone */ }

    // Parse the `code` param out of the deep-link URL. We can't pass
    // the full URL to exchangeCodeForSession — the modern Supabase JS
    // SDK (v2.100+) signature is `exchangeCodeForSession(authCode: string)`,
    // a bare code, not a URL. Custom-scheme URLs also can't be parsed
    // by the URL constructor in some Android WebView builds. Extract
    // the code manually with a regex on the query string.
    const queryStart = url.indexOf('?');
    const params = new URLSearchParams(queryStart >= 0 ? url.slice(queryStart + 1) : '');
    const code = params.get('code');
    const oauthError = params.get('error_description') || params.get('error');

    if (oauthError) {
      toast.error(`Google sign-in: ${oauthError}`);
      return;
    }
    if (!code) {
      toast.error('Sign-in callback missing auth code');
      return;
    }

    const { error } = await supabase.auth.exchangeCodeForSession(code);
    if (error) {
      toast.error(`Sign-in failed: ${error.message}`);
      // Also leave the message on console for adb logcat.
      console.error('exchangeCodeForSession failed:', error);
      return;
    }
    toast.success('Signed in');
    // onAuthStateChange in AuthContext picks up the new session
    // automatically — no further work needed here.
  });

  return () => {
    cleanup.then((h) => h.remove()).catch(() => { /* noop */ });
  };
}
